"use client";

import { useEffect, useState } from "react";
import { Group, InlineLink, Line, SectionHeader } from "./atoms";
import { FONT_MONO, SET } from "./palette";
import {
  detectPerfTier,
  getPerfProfileStore,
  setUserPerfOverride,
} from "@/lib/perf/detect-perf-tier";
import type { PerfProfile, PerfTier } from "@/lib/perf/perf-profile";

const TIER_LABEL: Record<PerfTier, string> = {
  low: "Low",
  balanced: "Balanced",
  high: "High",
};

const TIER_TONE: Record<PerfTier, { fg: string; bg: string; border: string }> = {
  low: { fg: "#fbbf24", bg: "rgba(251,191,36,0.10)", border: "rgba(251,191,36,0.35)" },
  balanced: { fg: SET.violetLight, bg: SET.violetSoft, border: SET.violetGlow },
  high: { fg: "#86efac", bg: "rgba(134,239,172,0.10)", border: "rgba(134,239,172,0.35)" },
};

const OVERRIDE_OPTIONS: Array<{ value: PerfTier | null; label: string }> = [
  { value: null, label: "Auto" },
  { value: "low", label: "Low" },
  { value: "balanced", label: "Balanced" },
  { value: "high", label: "High" },
];

export function PerformanceSection() {
  // Subscribe to the live store so the auto-downgrade applied by the perf
  // coordinator is reflected here without a reload.
  const [profile, setProfile] = useState<PerfProfile | null>(null);
  const [autoDowngraded, setAutoDowngraded] = useState(false);
  const [autoTier, setAutoTier] = useState<PerfTier | null>(null);

  useEffect(() => {
    const store = getPerfProfileStore();
    setProfile(store.value);
    setAutoDowngraded(store.isAutoDowngraded());
    setAutoTier(detectPerfTier());

    return store.subscribe((next) => {
      setProfile(next);
      setAutoDowngraded(store.isAutoDowngraded());
    });
  }, []);

  const handleOverride = (tier: PerfTier | null) => {
    setUserPerfOverride(tier);
  };

  const handleRestore = () => {
    getPerfProfileStore().restoreBase();
  };

  if (!profile) {
    return null;
  }

  const isOverridden = profile.source === "user-forced";
  const currentOverride = isOverridden ? profile.tier : null;

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        kicker="Performance"
        trailing={<TierBadge tier={profile.tier} source={profile.source} />}
      />

      <Group>
        {autoDowngraded ? (
          <Line
            withDivider={false}
            label="Mode réduit auto-activé"
            hint="Performances dégradées détectées : particules et lazy-load coupés en cours de session"
            control={<InlineLink onClick={handleRestore}>Restaurer</InlineLink>}
          />
        ) : null}
        <Line
          withDivider={autoDowngraded}
          label="Profil détecté"
          hint={
            isOverridden
              ? `Auto suggérerait : ${autoTier ? TIER_LABEL[autoTier] : "—"}`
              : "Basé sur les capacités du device (cores, écran, plateforme)"
          }
          control={<Mono>{TIER_LABEL[profile.tier]}</Mono>}
        />
        <Line
          label="Antialias"
          control={<BoolPill on={profile.render.antialias} />}
        />
        <Line
          label="Reverb"
          hint="Tone.Reverb (convolution FFT par sample)"
          control={<BoolPill on={profile.audio.enableReverb} />}
        />
        <Line
          label="Sampler"
          hint={
            profile.audio.samplerStrategy === "lazy"
              ? "Charge des layers de vélocité supplémentaires à la demande"
              : "Un seul layer de vélocité (v8)"
          }
          control={
            <Mono>
              {profile.audio.samplerStrategy === "lazy" ? "Lazy" : "Single"}
            </Mono>
          }
        />
        <Line
          label="Budget audio"
          hint="Cap mémoire approximatif des AudioBuffers décodés"
          control={<Mono>{profile.audio.sampleBudgetMB} MB</Mono>}
        />
        <Line
          label="Particules"
          hint="Effets visuels (halos, particules) sur note jouée"
          control={<BoolPill on={profile.effects.particlesEnabled} />}
        />
      </Group>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: "12px 14px",
          borderRadius: 10,
          background: "rgba(255,255,255,0.02)",
          border: `1px solid ${SET.border}`,
        }}
      >
        <span style={{ fontSize: 12, color: SET.text }}>Forcer un profil</span>
        <span style={{ fontSize: 11, color: SET.textMute }}>
          Prend effet au prochain rechargement de la page.
        </span>

        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          {OVERRIDE_OPTIONS.map((opt) => {
            const active = opt.value === currentOverride;

            return (
              <button
                key={opt.label}
                type="button"
                onClick={() => handleOverride(opt.value)}
                style={{
                  flex: 1,
                  padding: "6px 10px",
                  fontFamily: FONT_MONO,
                  fontSize: 11,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: active ? SET.violetLight : SET.textDim,
                  background: active ? SET.violetSoft : "rgba(255,255,255,0.02)",
                  border: `1px solid ${active ? SET.violetGlow : SET.border}`,
                  borderRadius: 6,
                  cursor: "pointer",
                  transition: "color 150ms, background 150ms, border-color 150ms",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function TierBadge({ tier, source }: { tier: PerfTier; source: PerfProfile["source"] }) {
  const tone = TIER_TONE[tier];

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 8px",
        borderRadius: 999,
        fontFamily: FONT_MONO,
        fontSize: 10,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: tone.fg,
        background: tone.bg,
        border: `1px solid ${tone.border}`,
      }}
    >
      {TIER_LABEL[tier]}
      {source === "user-forced" ? (
        <span style={{ color: SET.textMute, fontSize: 9 }}>· forcé</span>
      ) : null}
    </span>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontFamily: FONT_MONO,
        fontSize: 11,
        color: SET.textDim,
        letterSpacing: "0.04em",
      }}
    >
      {children}
    </span>
  );
}

function BoolPill({ on }: { on: boolean }) {
  return (
    <span
      style={{
        fontFamily: FONT_MONO,
        fontSize: 10,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: on ? "#86efac" : SET.textMute,
        padding: "2px 8px",
        borderRadius: 999,
        background: on ? "rgba(134,239,172,0.10)" : "rgba(255,255,255,0.03)",
        border: `1px solid ${on ? "rgba(134,239,172,0.30)" : SET.border}`,
      }}
    >
      {on ? "Activé" : "Désactivé"}
    </span>
  );
}
